import React from 'react';
import { Link } from 'react-router-dom';
import './SchedulingInfoPage.css';

const SchedulingInfoPage: React.FC = () => {
  return (
    <div className="scheduling-info-page">
      <div className="hero-section scheduling">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">Free Scheduling Platform</h1>
            <p className="hero-description">Free booking links, calendar sync, and meeting scheduling. The simple alternative to expensive scheduling tools.</p>
            <div className="cta-buttons">
              <Link to="/scheduling" className="btn btn-primary btn-large">Open Scheduling</Link>
              <Link to="/" className="btn btn-secondary btn-large">Back to Home</Link>
            </div>
          </div>
        </div>
      </div>

      <section className="features-section">
        <div className="container">
          <h2>Core Features</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🔗</div>
              <h3>Booking Links</h3>
              <p>Share a simple link and let people book time with you. No back-and-forth emails needed.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>Calendar Sync</h3>
              <p>Connect your calendar to automatically show your real availability and prevent double bookings.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">👥</div>
              <h3>Team Scheduling</h3>
              <p>Coordinate meetings across multiple team members with round-robin and collective availability.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🗳️</div>
              <h3>Meeting Polls</h3>
              <p>Let participants vote on their preferred meeting times. Find the best time for everyone.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎉</div>
              <h3>Group Events</h3>
              <p>Create events that multiple people can register for, with automatic capacity management.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🔄</div>
              <h3>Routing Forms</h3>
              <p>Ask questions before booking to route people to the right meeting type or team member.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="workflow-section">
        <div className="container">
          <h2>How It Works</h2>
          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <h3>Create Your Profile</h3>
              <p>Set up your availability, time zone, and booking preferences in minutes.</p>
            </div>
            <div className="workflow-step">
              <div className="step-number">2</div>
              <h3>Share Your Link</h3>
              <p>Send your booking link via email, embed it on your website, or add it to your email signature.</p>
            </div>
            <div className="workflow-step">
              <div className="step-number">3</div>
              <h3>Get Booked</h3>
              <p>People pick a time that works for them. You both get confirmation and calendar invites.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="pricing-section">
        <div className="container">
          <h2>Simple Pricing</h2>
          <div className="pricing-grid">
            <div className="pricing-card free">
              <h3>Free</h3>
              <div className="price">$0</div>
              <ul>
                <li>Unlimited booking links</li>
                <li>Calendar sync</li>
                <li>Meeting polls</li>
                <li>Group events</li>
                <li>Email notifications</li>
              </ul>
              <Link to="/scheduling" className="btn btn-primary">Get Started Free</Link>
            </div>
            <div className="pricing-card supporter">
              <h3>Supporters Club</h3>
              <div className="price">$10/year</div>
              <ul>
                <li>Everything in Free</li>
                <li>Custom branding</li>
                <li>Priority support</li>
                <li>Advanced routing</li>
                <li>Support development</li>
              </ul>
              <Link to="/account" className="btn btn-premium">Join Supporters Club</Link>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Is this really free?</h3>
              <p>Yes. Core scheduling features are completely free with no hidden limits. We keep it free because we believe scheduling should be accessible to everyone.</p>
            </div>
            <div className="faq-item">
              <h3>How does this compare to Calendly?</h3>
              <p>We offer similar core features for free. No paywalls on basic functionality, no per-seat pricing, and no feature gates to push you into paid plans.</p>
            </div>
            <div className="faq-item">
              <h3>Which calendars are supported?</h3>
              <p>We support Google Calendar and Outlook/Microsoft 365 calendar integration for automatic availability sync.</p>
            </div>
            <div className="faq-item">
              <h3>Can I use this for my business?</h3>
              <p>Absolutely. The scheduling platform is built for professionals, freelancers, consultants, and teams of any size.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Cross-Tool Recommendations */}
      <section style={{padding: '3rem 2rem', background: '#f9fafb'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          <h2 style={{textAlign: 'center', marginBottom: '0.5rem'}}>Explore More Financial Tools</h2>
          <p style={{textAlign: 'center', color: '#6b7280', marginBottom: '2rem'}}>One account, all the tools you need</p>
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '1rem'}}>
            <Link to="/accounting" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Accounting</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Track income, expenses, and generate financial reports</p>
              </div>
            </Link>
            <Link to="/tax-tools" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Tax Tools</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Estimate income taxes and calculate payroll deductions</p>
              </div>
            </Link>
            <Link to="/career-comparison" style={{textDecoration: 'none', color: 'inherit'}}>
              <div style={{background: 'white', padding: '1.5rem', borderRadius: '8px', border: '1px solid #e5e7eb', transition: 'box-shadow 0.2s'}}>
                <h3 style={{color: '#dc2626', marginBottom: '0.5rem'}}>Career Comparison</h3>
                <p style={{color: '#6b7280', fontSize: '0.9rem', margin: 0}}>Compare job offers and career paths side-by-side</p>
              </div>
            </Link>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>Start Scheduling Today</h2>
          <p>Set up your booking page in minutes. No credit card required.</p>
          <div className="cta-buttons">
            <Link to="/scheduling" className="btn btn-primary btn-large">Open Scheduling</Link>
            <Link to="/account" className="btn btn-secondary btn-large">Create Free Account</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SchedulingInfoPage;
